import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, Instagram, MessageCircle, Send, FileText, MapPin } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import FooterLanguageSelector from "./FooterLanguageSelector";
import { useTranslation } from "react-i18next";

// Import translations
import fa from "./locales/Footer-fa.json";
import en from "./locales/Footer-en.json";
import ar from "./locales/Footer-ar.json";
import tr from "./locales/Footer-tr.json";

const Footer: React.FC = () => {
    const { t, i18n } = useTranslation();

    // Register component translations
    useEffect(() => {
        const ns = "Footer";
        const resources = { fa, en, ar, tr } as const;

        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const [hoveredColumn, setHoveredColumn] = useState<number | null>(null);

    // Determine text direction based on current language
    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const dir = isRTL ? "rtl" : "ltr";
    const textAlign = isRTL ? "text-right" : "text-left";

    // Get products items
    const productsItems = [];
    for (let i = 0; i < 10; i++) {
        const label = t(`sections.products.items.${i}.label`, { ns: "Footer", defaultValue: "" });
        const path = t(`sections.products.items.${i}.path`, { ns: "Footer", defaultValue: "" });
        if (label && path && label !== `sections.products.items.${i}.label`) {
            productsItems.push({ label, path });
        } else {
            break;
        }
    }

    // Get company items
    const companyItems = [];
    for (let i = 0; i < 10; i++) {
        const label = t(`sections.company.items.${i}.label`, { ns: "Footer", defaultValue: "" });
        const path = t(`sections.company.items.${i}.path`, { ns: "Footer", defaultValue: "" });
        if (label && path && label !== `sections.company.items.${i}.label`) {
            companyItems.push({ label, path });
        } else {
            break;
        }
    }

    // Get trust badges — now supports up to 4
    const trustBadges = [];
    for (let i = 0; i < 4; i++) {
        const alt = t(`sections.trust.badges.${i}.alt`, { ns: "Footer", defaultValue: "" });
        const image = t(`sections.trust.badges.${i}.image`, { ns: "Footer", defaultValue: "" });
        if (alt && image && alt !== `sections.trust.badges.${i}.alt`) {
            trustBadges.push({ alt, image });
        } else {
            break;
        }
    }

    // Get download items — now supports up to 3
    const downloadItems = [];
    for (let i = 0; i < 3; i++) {
        const key = ["android", "ios", "pwa"][i];
        const url = t(`sections.download.${key}.url`, { ns: "Footer", defaultValue: "" });
        const alt = t(`sections.download.${key}.alt`, { ns: "Footer", defaultValue: "" });
        const image = t(`sections.download.${key}.image`, { ns: "Footer", defaultValue: "" });
        if (url && alt && url !== `sections.download.${key}.url`) {
            downloadItems.push({ key, url, alt, image });
        }
    }

    return (
        <footer dir={dir} className="relative bg-gradient-to-b from-white/90 via-blue-50/40 to-blue-100/50 border-t border-blue-200/40 py-16 backdrop-blur-md overflow-hidden">
            {/* Subtle atmospheric ambient glow */}
            <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-3/4 h-48 bg-blue-400/10 blur-3xl rounded-full" />

            <div className="container mx-auto px-4 sm:px-6 relative z-10">
                {/* ── Top grid ── */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-10">
                    {/* Brand + contact */}
                    <div className="col-span-1 md:col-span-4 flex flex-col gap-4">
                        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} viewport={{ once: true }}>
                            <Link
                                to="/biolink"
                                className="group inline-flex items-center space-x-2 cursor-pointer"
                                aria-label={t("logo.alt", { ns: "Footer", defaultValue: "Delband BioLink" })}
                            >
                                <motion.div
                                    whileHover={{ scale: 1.04 }}
                                    whileTap={{ scale: 0.96 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 18 }}
                                    className="relative"
                                >
                                    <img src="/media/logo-brand.svg" alt={t("logo.alt", { ns: "Footer" })} className="h-11 object-contain group-hover:brightness-110 transition-all duration-300" />
                                </motion.div>
                            </Link>
                        </motion.div>

                        <p className={`text-slate-600 text-sm leading-relaxed ${isRTL ? "text-justify" : "text-left"}`}>{t("description", { ns: "Footer" })}</p>

                        <div className="space-y-2.5 pt-1">
                            {/* Email */}
                            <a
                                href={`mailto:${t("email", { ns: "Footer" })}`}
                                className="group flex items-center text-slate-600 hover:text-blue-600 text-sm transition-colors duration-200"
                            >
                                <div className={`w-8 h-8 rounded-lg bg-blue-50 group-hover:bg-blue-600 text-blue-600 group-hover:text-white flex items-center justify-center transition-all duration-200 ${isRTL ? "ml-2.5" : "mr-2.5"} shrink-0 shadow-2xs`}>
                                    <Mail className="w-4 h-4" />
                                </div>
                                <span className="font-medium group-hover:text-blue-600 transition-colors">{t("email", { ns: "Footer" })}</span>
                            </a>

                            {/* Address */}
                            <div className="flex items-start text-slate-600 text-sm">
                                <div className={`w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center ${isRTL ? "ml-2.5" : "mr-2.5"} shrink-0 mt-0.5 shadow-2xs`}>
                                    <MapPin className="w-4 h-4" />
                                </div>
                                <span className={`leading-relaxed ${isRTL ? "text-right" : "text-left"}`}>{t("address", { ns: "Footer" })}</span>
                            </div>
                        </div>
                    </div>

                    {/* Products */}
                    <motion.div
                        className="col-span-1 md:col-span-2 p-3 sm:p-4 rounded-2xl bg-white/40 border border-white/60 shadow-xs backdrop-blur-sm transition-all duration-300 hover:bg-white/75 hover:border-blue-200/60 hover:shadow-md"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        viewport={{ once: true }}
                    >
                        <h3 className={`text-slate-900 font-bold mb-4 text-sm sm:text-base flex items-center gap-2 ${textAlign}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                            {t("sections.products.title", { ns: "Footer" })}
                        </h3>
                        <ul className="space-y-2.5">
                            {productsItems.map((item, index) => (
                                <motion.li key={index} whileHover={{ x: isRTL ? -4 : 4 }} transition={{ type: "spring", stiffness: 400, damping: 20 }}>
                                    <a href={item.path} className={`text-slate-600 hover:text-blue-600 font-medium transition-colors block text-sm py-0.5 ${textAlign}`}>
                                        {item.label}
                                    </a>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Company */}
                    <motion.div
                        className="col-span-1 md:col-span-2 p-3 sm:p-4 rounded-2xl bg-white/40 border border-white/60 shadow-xs backdrop-blur-sm transition-all duration-300 hover:bg-white/75 hover:border-blue-200/60 hover:shadow-md"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.15 }}
                        viewport={{ once: true }}
                    >
                        <h3 className={`text-slate-900 font-bold mb-4 text-sm sm:text-base flex items-center gap-2 ${textAlign}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                            {t("sections.company.title", { ns: "Footer" })}
                        </h3>
                        <ul className="space-y-2.5">
                            {companyItems.map((item, index) => (
                                <motion.li key={index} whileHover={{ x: isRTL ? -4 : 4 }} transition={{ type: "spring", stiffness: 400, damping: 20 }}>
                                    <a href={item.path} className={`text-slate-600 hover:text-blue-600 font-medium transition-colors block text-sm py-0.5 ${textAlign}`}>
                                        {item.label}
                                    </a>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Download */}
                    <motion.div
                        className="col-span-1 md:col-span-2 p-3 sm:p-4 rounded-2xl bg-white/40 border border-white/60 shadow-xs backdrop-blur-sm transition-all duration-300 hover:bg-white/75 hover:border-blue-200/60 hover:shadow-md"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        <h3 className={`text-slate-900 font-bold mb-4 text-sm sm:text-base flex items-center gap-2 ${textAlign}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                            {t("sections.download.title", { ns: "Footer" })}
                        </h3>
                        <div className="flex flex-col space-y-2.5 items-center">
                            {/* Android */}
                            <motion.a href={t("sections.download.android.url", { ns: "Footer" })} className="block w-full" whileHover={{ scale: 1.03, y: -1 }} whileTap={{ scale: 0.97 }}>
                                <img src="/media/android download.webp" alt={t("sections.download.android.alt", { ns: "Footer" })} className="w-[160px] h-auto object-contain max-h-14 mx-auto drop-shadow-xs hover:drop-shadow-md transition-all" />
                            </motion.a>
                            {/* PWA */}
                            <motion.a href={t("sections.download.pwa.url", { ns: "Footer" })} className="block w-full" whileHover={{ scale: 1.03, y: -1 }} whileTap={{ scale: 0.97 }}>
                                <img src="/media/pwa-Badge-2-01.webp" alt={t("sections.download.pwa.alt", { ns: "Footer" })} className="rounded-xl w-[160px] h-auto object-cover max-h-[50px] mx-auto drop-shadow-xs hover:drop-shadow-md transition-all" />
                            </motion.a>
                            {/* iOS */}
                            <motion.a href={t("sections.download.ios.url", { ns: "Footer" })} className="block w-full" whileHover={{ scale: 1.03, y: -1 }} whileTap={{ scale: 0.97 }}>
                                <img src="/media/Sibirani-Badge-2-01.webp" alt={t("sections.download.ios.alt", { ns: "Footer" })} className="w-[160px] h-auto object-contain max-h-14 mx-auto drop-shadow-xs hover:drop-shadow-md transition-all" />
                            </motion.a>
                        </div>
                    </motion.div>

                    {/* Trust badges */}
                    <motion.div
                        className="col-span-1 md:col-span-2 p-3 sm:p-4 rounded-2xl bg-white/40 border border-white/60 shadow-xs backdrop-blur-sm transition-all duration-300 hover:bg-white/75 hover:border-blue-200/60 hover:shadow-md"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.25 }}
                        viewport={{ once: true }}
                    >
                        <h3 className={`text-slate-900 font-bold mb-4 text-sm sm:text-base flex items-center gap-2 ${textAlign}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                            {t("sections.trust.title", { ns: "Footer" })}
                        </h3>
                        <div className="grid grid-cols-2 gap-2.5">
                            {trustBadges.map((badge, index) => (
                                <motion.a
                                    key={index}
                                    href="#"
                                    className="flex items-center justify-center bg-white/80 backdrop-blur-sm hover:bg-white rounded-xl p-2.5 transition-all duration-300 border border-blue-100/50 hover:border-blue-400 hover:shadow-md group"
                                    whileHover={{ scale: 1.04, y: -2 }}
                                    whileTap={{ scale: 0.96 }}
                                >
                                    <img src={badge.image} alt={badge.alt} className="h-10 object-contain group-hover:brightness-105 transition-all" />
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* ── Bottom bar ── */}
                <motion.div
                    className="border-t border-blue-200/50 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.3 }}
                    viewport={{ once: true }}
                >
                    <div className="flex items-center gap-3">
                        <FooterLanguageSelector />
                        <p className={`text-slate-500 text-xs sm:text-sm ${textAlign}`}>{t("copyright", { ns: "Footer" })}</p>
                    </div>

                    <div className={`flex items-center gap-2.5 ${isRTL ? "space-x-reverse" : ""}`}>
                        {/* Instagram */}
                        <motion.a
                            href={t("social.instagram.url", { ns: "Footer" })}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-9 h-9 rounded-xl bg-white/80 hover:bg-blue-600 text-slate-500 hover:text-white border border-blue-100 hover:border-blue-600 flex items-center justify-center transition-all duration-300 shadow-2xs hover:shadow-md"
                            whileHover={{ scale: 1.1, y: -2 }}
                            whileTap={{ scale: 0.92 }}
                            aria-label={t("social.instagram.label", { ns: "Footer" })}
                        >
                            <Instagram className="w-4 h-4" />
                        </motion.a>

                        {/* Bale */}
                        <motion.a
                            href={t("social.bale.url", { ns: "Footer" })}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-9 h-9 rounded-xl bg-white/80 hover:bg-blue-600 text-slate-500 hover:text-white border border-blue-100 hover:border-blue-600 flex items-center justify-center transition-all duration-300 shadow-2xs hover:shadow-md group"
                            whileHover={{ scale: 1.1, y: -2 }}
                            whileTap={{ scale: 0.92 }}
                            aria-label={t("social.bale.label", { ns: "Footer" })}
                        >
                            <img src="/icon/bale-border.svg" alt={t("social.bale.label", { ns: "Footer" })} className="w-4 h-4 group-hover:brightness-0 group-hover:invert transition-all" />
                        </motion.a>

                        {/* WhatsApp */}
                        <motion.a
                            href={t("social.whatsapp.url", { ns: "Footer" })}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-9 h-9 rounded-xl bg-white/80 hover:bg-blue-600 text-slate-500 hover:text-white border border-blue-100 hover:border-blue-600 flex items-center justify-center transition-all duration-300 shadow-2xs hover:shadow-md"
                            whileHover={{ scale: 1.1, y: -2 }}
                            whileTap={{ scale: 0.92 }}
                            aria-label={t("social.whatsapp.label", { ns: "Footer" })}
                        >
                            <FaWhatsapp className="w-4 h-4" />
                        </motion.a>

                        {/* Catalog */}
                        <motion.a
                            href={t("social.catalog.url", { ns: "Footer" })}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-9 h-9 rounded-xl bg-white/80 hover:bg-blue-600 text-slate-500 hover:text-white border border-blue-100 hover:border-blue-600 flex items-center justify-center transition-all duration-300 shadow-2xs hover:shadow-md"
                            whileHover={{ scale: 1.1, y: -2 }}
                            whileTap={{ scale: 0.92 }}
                            aria-label={t("social.catalog.label", { ns: "Footer" })}
                        >
                            <FileText className="w-4 h-4" />
                        </motion.a>
                    </div>
                </motion.div>
            </div>
        </footer>
    );
};

export default Footer;
